import type { DataSource, Entity, WebcamEntity } from '../types/entity';

/**
 * WSDOT Traveler Information API — Highway Cameras. O "AccessCode" é
 * passado como query param por design da própria API pública do estado
 * de Washington, o que a coloca no mesmo grupo da Windy: key de uso
 * client-side legítimo, não segredo de billing (exceção documentada em
 * app/.env.example).
 *
 * Docs: https://wsdot.wa.gov/traffic/api/Documentation/group___highway_cameras.html
 * Schema confirmado contra:
 *   https://wsdot.wa.gov/traffic/api/Documentation/class_camera.html
 *   https://wsdot.wa.gov/traffic/api/Documentation/class_roadway_location.html
 *
 * CORRIGIDO após validação (divergia da suposição inicial):
 * - Campos são PascalCase (padrão .NET), não camelCase.
 * - A API expõe DUAS coordenadas diferentes, não uma:
 *     `DisplayLatitude`/`DisplayLongitude` (raiz do objeto Camera) —
 *       doc oficial: "Latitude/Longitude of where to display the
 *       camera on a map". É a certa pra plotagem.
 *     `CameraLocation.Latitude`/`Longitude` (dentro de RoadwayLocation)
 *       — localização da rodovia em si, pode divergir um pouco do
 *       ponto de exibição ideal.
 *   Usamos Display* abaixo, por ser literalmente desenhada pra isso.
 * - `CameraLocation` é do tipo RoadwayLocation, que também carrega
 *   `Description`, `RoadName`, `Direction`, `MilePost` — úteis pro
 *   label/popup, não só coordenada.
 * - Não existe campo de timestamp de atualização na classe Camera —
 *   `observedAt` fica com a hora do fetch (a API não informa quando a
 *   imagem foi tirada, só sua URL estável).
 *
 * Sem filtro de bounds na API — o dataset inteiro (~1.700 câmeras) é
 * pequeno o bastante pra trazer tudo e deixar o mapa cuidar do recorte
 * visual por viewport.
 */
const WSDOT_ENDPOINT =
  'https://wsdot.wa.gov/Traffic/api/HighwayCameras/HighwayCamerasREST.svc/GetCamerasAsJson';

interface WsdotRoadwayLocation {
  Description: string | null;
  RoadName: string | null;
  Direction: string | null;
  MilePost: number;
  Latitude: number;
  Longitude: number;
}

interface WsdotCameraItem {
  CameraID: number;
  Title: string;
  Description: string | null;
  ImageURL: string;
  DisplayLatitude: number;
  DisplayLongitude: number;
  CameraLocation: WsdotRoadwayLocation;
  IsActive: boolean;
}

function toWebcamEntity(item: WsdotCameraItem): WebcamEntity {
  return {
    id: `webcam-wsdot-${item.CameraID}`,
    layer: 'webcams-wsdot',
    lat: item.DisplayLatitude,
    lon: item.DisplayLongitude,
    label: item.Title,
    severity: 'normal',
    observedAt: new Date().toISOString(),
    raw: {
      title: item.Title,
      previewUrl: item.ImageURL || null,
      playerUrl: null,
      source: 'wsdot',
    },
  };
}

export function createWsdotWebcamsSource(accessCode: string): DataSource {
  return {
    layer: 'webcams-wsdot',
    label: 'Câmeras (WSDOT)',
    requiresClientKey: true,
    viaCloudFunction: false,

    async fetchEntities(): Promise<Entity[]> {
      const url = new URL(WSDOT_ENDPOINT);
      url.searchParams.set('AccessCode', accessCode);

      const res = await fetch(url.toString());
      if (!res.ok) {
        throw new Error(`WSDOT respondeu ${res.status}`);
      }

      const data = (await res.json()) as WsdotCameraItem[];
      return data
        .filter((item) => item.IsActive && item.DisplayLatitude && item.DisplayLongitude)
        .map(toWebcamEntity);
    },
  };
}
